/**
 * Customisation: ContentMetadataTags
 *
 * Drop into:
 *   src/customizations/volto/components/theme/ContentMetadataTags/ContentMetadataTags.jsx
 *
 * Adds an og:image fallback chain:
 *   1. lead_image
 *   2. preview_image
 *   3. preview_image_link (linked image relation)
 *   4. image (generic — e.g. Image content type)
 *
 * Images are validated against OG_IMAGE_MIN_WIDTH / OG_IMAGE_MIN_HEIGHT before
 * use. A console.warn fires only when something was expected but couldn't be
 * resolved — not for content that simply has no images.
 */

import React from 'react';
import { Helmet } from '@plone/volto/helpers';
import { flattenToAppURL, toBackendLang } from '@plone/volto/helpers';
import config from '@plone/volto/registry';

// Minimum dimensions for og:image to be picked up by social platforms.
// Facebook requires 200×200; 600×315 is the practical floor for decent previews.
const OG_IMAGE_MIN_WIDTH = 600;
const OG_IMAGE_MIN_HEIGHT = 315;

/**
 * Given a Plone image scales object, return the URL of the smallest scale
 * that still meets the og:image minimum dimensions.
 *
 * Preference order: large → great → huge → preview → any qualifying scale.
 * Falls back to the raw downloadUrl if present (dimensions then unknown).
 * Warns when scales exist but none meet the minimum — that's a content issue
 * worth surfacing.
 *
 * @param {object|null} scalesObj  - content.image_field.scales
 * @param {string|null} downloadUrl - content.image_field.download
 * @returns {string|null}
 */
function getOgImageUrl(scalesObj, downloadUrl) {
  if (!scalesObj && !downloadUrl) return null;

  const preferredOrder = ['large', 'great', 'huge', 'preview'];

  for (const scaleName of preferredOrder) {
    const scale = scalesObj?.[scaleName];
    if (
      scale?.download &&
      scale.width >= OG_IMAGE_MIN_WIDTH &&
      scale.height >= OG_IMAGE_MIN_HEIGHT
    ) {
      return flattenToAppURL(scale.download);
    }
  }

  // Try any remaining scale that qualifies.
  if (scalesObj) {
    const qualifying = Object.values(scalesObj).find(
      (s) =>
        s?.download &&
        s.width >= OG_IMAGE_MIN_WIDTH &&
        s.height >= OG_IMAGE_MIN_HEIGHT,
    );
    if (qualifying) return flattenToAppURL(qualifying.download);
  }

  // We had scale data but nothing met the minimum — worth knowing about.
  if (scalesObj) {
    console.warn(
      '[ContentMetadataTags] Image found but no scale meets og:image minimums ' +
        `(${OG_IMAGE_MIN_WIDTH}×${OG_IMAGE_MIN_HEIGHT}px). Scales available:`,
      Object.entries(scalesObj).map(([k, v]) => `${k}: ${v?.width}×${v?.height}`),
    );
  }

  // Fall back to raw download URL — dimensions unknown, but better than nothing.
  if (downloadUrl) return flattenToAppURL(downloadUrl);

  return null;
}

/**
 * Walk the fallback chain to find the best og:image for this content item.
 *
 * Chain:
 *   1. lead_image
 *   2. preview_image
 *   3. preview_image_link (linked image relation — may or may not be expanded)
 *   4. image (generic fallback, e.g. Image content type)
 *
 * @param {object} content - Plone REST API content object
 * @returns {string|null}
 */
function resolveOgImage(content) {
  // 1. Lead image
  if (content.lead_image?.scales) {
    const url = getOgImageUrl(content.lead_image.scales, content.lead_image.download);
    if (url) return url;
  }

  // 2. Preview image (inline)
  if (content.preview_image?.scales) {
    const url = getOgImageUrl(
      content.preview_image.scales,
      content.preview_image.download,
    );
    if (url) return url;
  }

  // 3. Preview image link (linked image relation).
  //    The back-end may or may not expand this — handle both cases.
  if (content.preview_image_link) {
    if (content.preview_image_link.scales) {
      // Back-end expanded the relation — we have scales, treat it normally.
      const url = getOgImageUrl(
        content.preview_image_link.scales,
        content.preview_image_link.download,
      );
      if (url) return url;
    } else if (content.preview_image_link['@id']) {
      // Relation present but not expanded by the serialiser.
      // Construct a best-guess URL using the large scale.
      // We can't validate dimensions here, so this is silent best-effort.
      return flattenToAppURL(
        `${content.preview_image_link['@id']}/@@images/image/large`,
      );
    } else {
      // Something arrived in preview_image_link but we can't use it.
      // Log it so it's debuggable without being noisy in normal operation.
      console.warn(
        '[ContentMetadataTags] preview_image_link is present but has neither ' +
          'scales nor @id — cannot resolve og:image from it.',
        content.preview_image_link,
      );
    }
  }

  // 4. Generic image field (Image content type, or any type with `image`)
  if (content.image?.scales) {
    const url = getOgImageUrl(content.image.scales, content.image.download);
    if (url) return url;
  }

  return null;
}

/**
 * ContentMetadataTags
 *
 * Renders <head> metadata tags for SEO and social sharing.
 * Replaces the Volto core component of the same name.
 */
const ContentMetadataTags = (props) => {
  const { content } = props;
  const { settings } = config;

  const ogImage = resolveOgImage(content);
  const lang = toBackendLang(content.language?.token || settings.defaultLanguage);

  return (
    <Helmet>
      {/* Language */}
      <html lang={lang} />

      {/* Standard meta */}
      {content.title && <title>{content.title}</title>}
      {content.description && (
        <meta name="description" content={content.description} />
      )}

      {/* Open Graph */}
      {content.title && <meta property="og:title" content={content.title} />}
      {content.description && (
        <meta property="og:description" content={content.description} />
      )}
      <meta property="og:type" content="website" />
      {ogImage && <meta property="og:image" content={ogImage} />}

      {/* Twitter / X card */}
      <meta name="twitter:card" content="summary_large_image" />
      {content.title && <meta name="twitter:title" content={content.title} />}
      {content.description && (
        <meta name="twitter:description" content={content.description} />
      )}
      {ogImage && <meta name="twitter:image" content={ogImage} />}

      {/* Robots — respect exclude_from_nav as a basic noindex signal */}
      {content.exclude_from_nav && (
        <meta name="robots" content="noindex, follow" />
      )}
    </Helmet>
  );
};

export default ContentMetadataTags;
